import React, { useState } from 'react';
import { Box, Button, Link as MuiLink, Stack, Typography } from '@mui/material';
import { tokens } from '../../../../theme/tokens';
import { StagedSourceBadge } from '../../../StagedSourceBadge';
import { DetailCard, KeyValueRows, LinkButton, TabBody, TextInput } from '../primitives';
import type { ProductDraft } from '../productDraft';
import type { AdminCanonicalProduct } from '../productDetailTypes';
import { formatDateTime } from '../productDetailFormat';

type JsonView = 'full' | 'raw' | 'specs';

interface Props {
  product: AdminCanonicalProduct;
  draft: ProductDraft;
  editing: boolean;
  update: (patch: Partial<ProductDraft>) => void;
  releaseLocks: (paths: string[]) => void;
  releasing: boolean;
  toast: (message: string) => void;
}

const PROVENANCE_LABELS: Record<string, string> = {
  description: 'description',
  shortDescription: 'shortDescription',
  features: 'features',
  images: 'images',
  specs: 'specifications',
};

const segBtn = (active: boolean) => ({
  height: 28,
  px: 1.5,
  minWidth: 0,
  borderRadius: '7px',
  fontSize: 12.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  color: active ? tokens.ink.primary : tokens.ink.muted,
  bgcolor: active ? tokens.surface.card : 'transparent',
  boxShadow: active ? tokens.shadow.card : 'none',
  '&:hover': { bgcolor: active ? tokens.surface.card : tokens.line.hairline },
});

export const SourceHistoryTab: React.FC<Props> = ({ product, draft, editing, update, releaseLocks, releasing, toast }) => {
  const [view, setView] = useState<JsonView>('full');
  const [wrap, setWrap] = useState(false);
  const [copied, setCopied] = useState(false);
  const source = product.source || {};
  const provenance = Object.entries(product.provenance || {}).filter(([, v]) => !!v);
  const locks = Object.entries(product.manualFields || {}).sort(([a], [b]) => a.localeCompare(b));

  const jsonValue = view === 'full' ? product : view === 'raw' ? source.rawData ?? {} : product.specificationsRaw ?? {};
  // While editing, the full document (every field the tabs above do not edit) and
  // specificationsRaw are editable JSON; source.rawData stays read-only.
  const editableView = editing && (view === 'full' || view === 'specs');
  const jsonText = editableView ? (view === 'full' ? draft.documentJson : draft.specificationsRawJson) : JSON.stringify(jsonValue, null, 2);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast('Copy failed: the browser blocked clipboard access');
    }
  };

  return (
    <TabBody>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ flex: '1 1 360px', minWidth: 0 }}>
          <DetailCard title="Source" note="Where this product was scraped from">
            <Box sx={{ px: '18px', pb: 1.5 }}>
              <KeyValueRows
                rows={[
                  { k: 'brand', v: product.brand },
                  // Only when the source resells another maker's product (MOT-633).
                  ...(product.distributor?.name ? [{ k: 'distributor', v: product.distributor.name }] : []),
                  { k: 'vendorId', v: source.vendorId },
                  { k: 'connectorId', v: source.connectorId },
                  { k: 'sourceId', v: source.sourceId },
                  {
                    k: 'sourceUrl',
                    v: source.sourceUrl ? (
                      <MuiLink href={source.sourceUrl} target="_blank" rel="noreferrer" underline="hover" sx={{ fontSize: 13 }}>
                        {source.sourceUrl}
                      </MuiLink>
                    ) : undefined,
                  },
                  { k: 'syncVersion', v: source.syncVersion != null ? `v${source.syncVersion}` : undefined },
                  { k: 'lastSyncedAt', v: formatDateTime(source.lastSyncedAt) },
                ]}
              />
            </Box>
          </DetailCard>
        </Box>
        <Box sx={{ flex: '1 1 360px', minWidth: 0 }}>
          <DetailCard title="Fingerprints & dates" note="Unchanged re-scrapes are skipped by fingerprint">
            <Box sx={{ px: '18px', pb: 1.5 }}>
              <KeyValueRows
                rows={[
                  { k: 'fingerprint.content', v: product.scrapeFingerprints?.content },
                  { k: 'fingerprint.specs', v: product.scrapeFingerprints?.specs },
                  { k: 'createdAt', v: formatDateTime(product.createdAt) },
                  { k: 'promotedAt', v: formatDateTime(product.promotedAt) },
                  { k: 'updatedAt', v: formatDateTime(product.updatedAt) },
                ]}
              />
            </Box>
          </DetailCard>
        </Box>
      </Box>

      <DetailCard
        title="Manual fields"
        count={locks.length}
        note="Saved by an admin or accepted from AI. Staged updates skip these fields when promoted."
        actions={
          locks.length > 1 ? (
            <LinkButton disabled={releasing} onClick={() => releaseLocks(locks.map(([path]) => path))}>
              Release all
            </LinkButton>
          ) : undefined
        }
      >
        {locks.length === 0 ? (
          <Typography sx={{ px: '18px', pb: 2, fontSize: 12.5, color: tokens.ink.muted }}>
            No manual fields. Every field takes the next staged update.
          </Typography>
        ) : (
          <Box sx={{ pb: 0.75 }}>
            {locks.map(([path, entry]) => (
              <Box key={path} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 40, px: '18px', borderTop: `1px solid ${tokens.line.faint}`, flexWrap: 'wrap' }}>
                <Typography component="span" sx={{ flex: '1 1 180px', fontFamily: tokens.font.mono, fontSize: 12, color: tokens.ink.primary }}>{path}</Typography>
                <StagedSourceBadge source={{ source: entry.source === 'ai' ? 'ai' : 'manual' }} />
                <Typography component="span" sx={{ fontSize: 12, color: tokens.ink.muted, fontVariantNumeric: 'tabular-nums' }}>
                  {[formatDateTime(entry.at), entry.by].filter(Boolean).join(' · ')}
                </Typography>
                <LinkButton sx={{ fontSize: 12 }} disabled={releasing} onClick={() => releaseLocks([path])} title="The next staged update may change this field again">
                  Release
                </LinkButton>
              </Box>
            ))}
          </Box>
        )}
      </DetailCard>

      <DetailCard title="Field provenance" note="provenance · who authored each content field">
        {provenance.length === 0 ? (
          <Typography sx={{ px: '18px', pb: 2, fontSize: 12.5, lineHeight: 1.5, color: tokens.ink.muted }}>
            Not recorded yet. Provenance is written when a staged update is promoted, so this product gets it on its next promotion.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto', pb: 0.75 }}>
            <Box sx={{ minWidth: 480 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(160px, 1fr) 140px 110px 160px', md: 'minmax(160px, 1fr) 140px 110px 160px' }, gap: 1.5, px: '18px', pb: 0.75, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>
                <span>Field</span>
                <span>From</span>
                <span>Confidence</span>
                <span>Set</span>
              </Box>
              {provenance.map(([key, entry]) => (
                <Box key={key} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(160px, 1fr) 140px 110px 160px', md: 'minmax(160px, 1fr) 140px 110px 160px' }, gap: 1.5, alignItems: 'center', minHeight: 38, px: '18px', borderTop: `1px solid ${tokens.line.faint}`, fontSize: 12.5 }}>
                  <Typography component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 12 }}>{PROVENANCE_LABELS[key] || key}</Typography>
                  <Box><StagedSourceBadge source={{ source: entry!.source === 'scraped' ? 'scraped' : entry!.source }} /></Box>
                  <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{entry!.confidence != null ? `${entry!.confidence}%` : '—'}</Typography>
                  <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.muted, fontVariantNumeric: 'tabular-nums' }}>{formatDateTime(entry!.at) || '—'}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DetailCard>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Raw JSON</Typography>
          <Box role="tablist" aria-label="JSON view" sx={{ display: 'inline-flex', p: '3px', borderRadius: '9px', bgcolor: tokens.surface.tint, gap: '2px' }}>
            {([
              ['full', 'Full document'],
              ['raw', 'source.rawData'],
              ['specs', 'specificationsRaw'],
            ] as const).map(([id, label]) => (
              <Button key={id} role="tab" aria-selected={view === id} onClick={() => setView(id)} sx={segBtn(view === id)}>
                {label}
              </Button>
            ))}
          </Box>
          <Typography sx={{ flex: '1 1 200px', fontSize: 12, color: tokens.ink.muted }}>
            {editableView
              ? view === 'full'
                ? 'Editable: fields changed here are saved too, but a field you also edited in a tab keeps the tab’s value. Removing a key does not delete it. Checked when you save.'
                : 'Editable. Must be a valid JSON object; checked when you save.'
              : editing && view === 'raw'
                ? 'source.rawData is read-only; edit it through the full document.'
                : 'Advanced fallback for anything without a field above.'}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setWrap((w) => !w)}
            aria-pressed={wrap}
            sx={{ height: 30, borderRadius: `${tokens.radius.small}px`, fontSize: 12.5, fontWeight: 600, ...(wrap ? { bgcolor: tokens.accent.tint, borderColor: tokens.accent.border, color: tokens.accent.hover } : { color: tokens.ink.body, borderColor: tokens.line.default }) }}
          >
            Wrap lines
          </Button>
          <Button size="small" variant="outlined" onClick={copy} sx={{ height: 30, borderRadius: `${tokens.radius.small}px`, fontSize: 12.5, fontWeight: 600, color: tokens.ink.body, borderColor: tokens.line.default }}>
            {copied ? 'Copied' : 'Copy JSON'}
          </Button>
        </Stack>
        {editableView ? (
          <TextInput
            ariaLabel={view === 'full' ? 'Full document JSON' : 'specificationsRaw JSON'}
            multiline
            rows={20}
            mono
            value={jsonText}
            onChange={(value) => update(view === 'full' ? { documentJson: value } : { specificationsRawJson: value })}
            sx={{ '& textarea': { whiteSpace: wrap ? 'pre-wrap' : 'pre', lineHeight: 1.65 } }}
          />
        ) : (
          <Box
            component="pre"
            sx={{
              m: 0,
              maxHeight: 480,
              px: 2,
              py: 1.75,
              border: `1px solid ${tokens.line.default}`,
              borderRadius: `${tokens.radius.card}px`,
              bgcolor: tokens.surface.sunken,
              overflow: 'auto',
              fontFamily: tokens.font.mono,
              fontSize: 12,
              lineHeight: 1.65,
              color: tokens.ink.primary,
              whiteSpace: wrap ? 'pre-wrap' : 'pre',
              overflowWrap: wrap ? 'anywhere' : 'normal',
            }}
          >
            {jsonText}
          </Box>
        )}
      </Box>
    </TabBody>
  );
};
